import ModalContent from '@/components/modal/ModalContent.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import { useFieldArray, useFormContext } from 'react-hook-form';
import React from 'react';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';
import { DropdownCustomFieldInputData } from '@/features/dashboard/tourCustomFields/schemas/dropdownCustomFieldSchema.ts';
import { Plus, Trash } from 'lucide-react';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import PrimitiveSingleSelect from '@/components/form/_primitives/PrimitiveSingleSelect.tsx';

const DropdownCustomFieldForm = () => {
  const {
    register,
    formState: { errors },
    control,
  } = useFormContext<DropdownCustomFieldInputData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
  });

  return (
    <>
      <ModalContent>
        <div className="text-sm text-neutral-600">
          Skonfiguruj jak powinno wyświetlać się to pole w formularzu.
        </div>

        <TextInput
          label="Nazwa pola"
          register={register('fieldName')}
          placeholder="Wpisz nazwę pola..."
          required={true}
          error={errors.fieldName?.message}
          hint="Tak będzie oznaczone to pole na formularzu "
        />

        <CheckboxInput
          label="Pole wymagane"
          register={register('isFieldMandatory')}
          error={errors.isFieldMandatory?.message}
        />

        <TextInput
          label="Sugerowana treść"
          register={register('placeholder')}
          placeholder="Wpisz sugerowaną treść..."
          error={errors.placeholder?.message}
          hint="Pojawi się w polu przed rozpoczęciem wypełniania pola"
        />

        <TextInput
          label="Tekst pomocniczy"
          register={register('hint')}
          placeholder="Wpisz tekst pomocniczy..."
          error={errors.hint?.message}
          hint="Pojawi się poniżej pola, może wyjaśnić uczestnikowi jak powinien wypełnić pole"
        />
      </ModalContent>
      <ModalContent className="border-y border-neutral-100">
        <h3 className="text-xl font-semibold leading-[120%]">
          Dodaj opcje do wyboru
        </h3>

        <div className="space-y-1.5">
          {fields.map((field, index) => (
            <div key={field.id} className="flex flex-row items-end gap-2">
              <TextInput
                register={register(`options.${index}.label`)}
                placeholder="Wpisz opcję..."
                error={errors.options?.[index]?.label?.message}
              />
              <DangerIconButton onClick={() => remove(index)}>
                <Trash />
              </DangerIconButton>
            </div>
          ))}

          <ActionButton
            tone="outline"
            size="sm"
            onClick={() => append({ label: '' })}
          >
            <Plus />
            Dodaj pole dodatkowe
          </ActionButton>
        </div>

        <InputError
          error={errors.options?.root?.message || errors.options?.message}
        />
      </ModalContent>
      <ModalContent>
        <h3 className="text-xl font-semibold leading-[120%]">Podgląd</h3>
      </ModalContent>
      <ModalContent className="bg-neutral-100">
        <CustomFieldDropdownPrimitiveInputPreview fields={fields} />
      </ModalContent>
    </>
  );
};

const CustomFieldDropdownPrimitiveInputPreview = ({
  fields,
}: {
  fields: { label: string; id?: string }[];
}) => {
  const { watch } = useFormContext<DropdownCustomFieldInputData>();

  const [selectedOption, setSelectedOption] = React.useState<
    string | undefined
  >(undefined);

  const fieldName = watch('fieldName');

  let title = 'Wpisz nazwe pola';
  if (fieldName && fieldName.length > 0) {
    title = fieldName;
  }

  const watchedOptions = watch('options');

  const displayFields = watchedOptions
    .filter((f) => f.label.length > 0)
    .map((field, index) => ({
      label: field.label,
      value: fields[index]?.id ?? field.label,
    }));

  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      <InputLabel label={title} required={watch('isFieldMandatory')} />
      <PrimitiveSingleSelect
        placeholder={watch('placeholder')}
        options={displayFields}
        className="w-full"
        value={selectedOption}
        onChange={setSelectedOption}
      />
      <InputHint hint={watch('hint')} />
    </div>
  );
};

export default DropdownCustomFieldForm;
